// ProposalReview — the review surface for one proposal batch (standing-agent-ui
// "Proposal review surface", "Batch decisions and execution", "Keyboard review",
// "Role-aware controls", "Live updates"; tasks 9.1–9.5). Renders as a workspace
// tab / deep link and inside a Blade from the Inbox (design D10).
//
// Server state is authoritative: every decision returns the batch and the page
// replaces its copy; live nudges refetch (never patch). Changes made by someone
// else are announced politely ("Proposal 1 approved by Dana") without moving
// focus. Callers key it by batch id, so a different batch starts fresh.

import { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Loader } from '@progress/kendo-react-indicators';
import { SvgIcon } from '@progress/kendo-react-common';
import { exclamationCircleIcon } from '@progress/kendo-svg-icons';
import type { ProposalBatch, StandingMeta } from '@shared/api-types';
import { formatDateTime } from '@shared/datetime';
import { ApiError } from '../../../lib/apiClient';
import { decideProposals, getBatch, runApproved, undoProposal } from '../../../lib/standingAgentApi';
import { useNow } from '../../../lib/useNow';
import { APPROVER_ONLY_REASON, canApprove as roleCanApprove, timeLeft } from '../pulsarUi';
import { useStandingAgentLive } from '../useStandingAgentLive';
import { ProposalCard, type CardPanel } from './ProposalCard';
import { announcements, bulkApprovalPlan, bulkSummary, isUndoable, tallyText } from './reviewModel';
import { useReviewShortcuts } from './useReviewShortcuts';
import '../StandingAgents.css';

export interface ProposalReviewProps {
  batchId: string;
  role: StandingMeta['role'] | null;
  /** Single-column layout (inside a Blade). */
  narrow?: boolean;
  /** Fires after any change this person makes (e.g. the Inbox refreshes its count). */
  onChanged?: () => void;
}

const SHORTCUTS: [string, string][] = [
  ['J', 'Next proposal'],
  ['K', 'Previous proposal'],
  ['A', 'Approve'],
  ['D', 'Deny (with an optional reason)'],
  ['E', 'Edit'],
  ['?', 'Show these shortcuts'],
];

export function ProposalReview({ batchId, role, narrow, onChanged }: ProposalReviewProps) {
  const now = useNow(60_000);
  const [batch, setBatch] = useState<ProposalBatch | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(0);
  const [panel, setPanel] = useState<{ id: string; kind: Exclude<CardPanel, null> } | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmDenyAll, setConfirmDenyAll] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const batchRef = useRef<ProposalBatch | null>(null);
  const cardRefs = useRef(new Map<string, HTMLElement>());
  const approver = roleCanApprove(role);

  /** Replace the batch; announce what changed since the last copy. */
  const accept = useCallback((next: ProposalBatch, ownChange: boolean) => {
    const said = announcements(batchRef.current, next);
    batchRef.current = next;
    setBatch(next);
    if (said.length > 0) setAnnouncement(said.join('. '));
    if (ownChange) onChanged?.();
  }, [onChanged]);

  const load = useCallback(async () => {
    try {
      accept(await getBatch(batchId), false);
      setLoadError(null);
    } catch (err) {
      if (!batchRef.current) setLoadError(err instanceof Error ? err.message : 'Failed to load the proposals.');
    }
  }, [batchId, accept]);
  const reseed = useStandingAgentLive(load, { agentId: batch?.agentId });

  const proposals = useMemo(() => [...(batch?.proposals ?? [])].sort((a, b) => a.ordinal - b.ordinal), [batch]);
  const undoable = batch ? isUndoable(batch) : false;
  const plan = useMemo(() => bulkApprovalPlan(proposals), [proposals]);
  const pendingCount = proposals.filter((p) => p.status === 'pending').length;
  const approvedCount = proposals.filter((p) => p.status === 'approved' || p.status === 'autoApproved').length;

  const act = async (fn: () => Promise<ProposalBatch>) => {
    setBusy(true);
    setError(null);
    try {
      accept(await fn(), true);
      setPanel(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('Someone else changed these proposals at the same time. They have been refreshed — check and try again.');
        void reseed();
      } else {
        setError(err instanceof Error ? err.message : 'That did not work.');
      }
    } finally {
      setBusy(false);
    }
  };

  const decide = (proposalId: string, decision: 'approve' | 'deny' | 'edit', extra: { reason?: string | null; editedArgs?: Record<string, unknown> } = {}) =>
    act(async () => (await decideProposals(batchId, { decisions: [{ proposalId, decision, ...extra }] })).batch);

  const focusCard = (index: number) => {
    const p = proposals[index];
    if (!p) return;
    setCurrent(index);
    cardRefs.current.get(p.id)?.focus();
  };

  const currentProposal = proposals[current];
  useReviewShortcuts(
    {
      next: () => focusCard(Math.min(current + 1, proposals.length - 1)),
      previous: () => focusCard(Math.max(current - 1, 0)),
      approve: () => {
        if (currentProposal?.status === 'pending' && approver && !busy) void decide(currentProposal.id, 'approve');
      },
      deny: () => {
        if (currentProposal?.status === 'pending') setPanel({ id: currentProposal.id, kind: 'deny' });
      },
      edit: () => {
        if (currentProposal?.status === 'pending' && approver) setPanel({ id: currentProposal.id, kind: 'edit' });
      },
      help: () => setShowHelp(true),
    },
    batch != null && !confirmAll && !confirmDenyAll && !showHelp,
  );

  if (loadError) return <p className="pz-error" role="alert">{loadError}</p>;
  if (!batch) return <Loader size="medium" type="converging-spinner" />;

  const expiry = timeLeft(batch.expiresUtc, now);
  const soon = expiry != null && expiry.hours < 12;

  return (
    <div className={`pz-review${narrow ? ' narrow' : ''}`}>
      <div className="pz-sr-only" aria-live="polite" role="status">
        {announcement}
      </div>

      <header className="pz-batch-head">
        <strong style={{ fontSize: 15 }}>
          {batch.agentName}
          {batch.checkInNumber != null && ` — check-in #${batch.checkInNumber}`}
          {batch.isTrial && ' (trial)'}
        </strong>
        <div className="pz-batch-meta">
          <span>
            Created <span className="pz-mono">{formatDateTime(batch.createdUtc)}</span>
          </span>
          {pendingCount > 0 &&
            (expiry ? (
              <span style={soon ? { color: 'var(--warn)', display: 'inline-flex', alignItems: 'center', gap: 4 } : undefined}>
                {soon && <SvgIcon icon={exclamationCircleIcon} size="xsmall" aria-hidden />}
                {soon ? 'Expires soon: ' : 'Expires in '}
                <span className="pz-mono">{expiry.text}</span>
              </span>
            ) : (
              <span>Expired</span>
            ))}
          {batch.droppedCount > 0 && <span>{batch.droppedCount} more were dropped (too many in one check-in)</span>}
        </div>
        {batch.summary && <p style={{ margin: '4px 0 0' }}>{batch.summary}</p>}
      </header>

      {error && <p className="pz-error" role="alert">{error}</p>}

      <ul className="pz-cards" aria-label="Proposals">
        {proposals.map((p, i) => (
          <li key={p.id}>
            <ProposalCard
              ref={(el) => {
                if (el) cardRefs.current.set(p.id, el);
                else cardRefs.current.delete(p.id);
              }}
              proposal={p}
              index={i + 1}
              agentName={batch.agentName}
              current={i === current}
              canApprove={approver}
              panel={panel?.id === p.id ? panel.kind : null}
              onPanel={(kind) => setPanel(kind ? { id: p.id, kind } : null)}
              onFocusCard={() => setCurrent(i)}
              onApprove={() => void decide(p.id, 'approve')}
              onDeny={(reason) => void decide(p.id, 'deny', { reason })}
              onEdit={(editedArgs) => void decide(p.id, 'edit', { editedArgs })}
              onUndo={() => void act(() => undoProposal(p.id))}
              undoable={undoable}
              busy={busy}
            />
          </li>
        ))}
      </ul>

      <footer className="pz-footer" aria-label="Decisions">
        <span className="pz-footer-tally" aria-live="polite">
          {tallyText(proposals)}
        </span>
        {pendingCount > 0 && (
          <>
            <Button
              type="button"
              aria-disabled={!approver || busy || plan.eligible.length === 0 ? true : undefined}
              title={!approver ? APPROVER_ONLY_REASON : plan.eligible.length === 0 ? 'Every remaining proposal needs its own review' : undefined}
              aria-describedby={!approver ? 'pz-footer-reason' : undefined}
              onClick={() => approver && !busy && plan.eligible.length > 0 && setConfirmAll(true)}
            >
              Approve all remaining
            </Button>
            <Button type="button" disabled={busy} onClick={() => setConfirmDenyAll(true)}>
              Deny all remaining
            </Button>
          </>
        )}
        {approvedCount > 0 && undoable && (
          <Button type="button" disabled={busy} onClick={() => void act(() => runApproved(batchId))}>
            Run approved now
          </Button>
        )}
        <Button type="button" fillMode="flat" onClick={() => setShowHelp(true)} aria-label="Keyboard shortcuts">
          <span className="pz-kbd">?</span>
        </Button>
        {!approver && pendingCount > 0 && (
          <span id="pz-footer-reason" className="pz-hint" style={{ flexBasis: '100%', margin: 0 }}>
            {APPROVER_ONLY_REASON}.
          </span>
        )}
      </footer>

      {confirmAll && (
        <Dialog title="Approve all remaining?" onClose={() => setConfirmAll(false)} width={480}>
          <p style={{ marginTop: 0 }} data-testid="bulk-summary">
            {bulkSummary(plan)}
          </p>
          {plan.held.length > 0 && (
            <div>
              <p style={{ marginBottom: 4 }}>
                {plan.held.length === 1 ? 'This one is' : `These ${plan.held.length} are`} left for individual review:
              </p>
              <ul>
                {plan.held.map((p) => (
                  <li key={p.id}>
                    {p.title} <span className="pz-mono">({p.tool})</span>
                    {p.risk === 'high' ? ' — high risk' : ' — always asks'}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="pz-hint">When every proposal is decided, the approved ones run.</p>
          <DialogActionsBar>
            <Button type="button" onClick={() => setConfirmAll(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              themeColor="primary"
              disabled={busy}
              onClick={() => {
                setConfirmAll(false);
                void act(async () => (await decideProposals(batchId, { all: { decision: 'approve' } })).batch);
              }}
            >
              Approve {plan.eligible.length}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {confirmDenyAll && (
        <Dialog title="Deny all remaining?" onClose={() => setConfirmDenyAll(false)} width={440}>
          <p style={{ marginTop: 0 }}>
            {pendingCount} pending proposal{pendingCount === 1 ? '' : 's'} will be denied. Nothing will change.
          </p>
          <DialogActionsBar>
            <Button type="button" onClick={() => setConfirmDenyAll(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              themeColor="primary"
              disabled={busy}
              onClick={() => {
                setConfirmDenyAll(false);
                void act(async () => (await decideProposals(batchId, { all: { decision: 'deny' } })).batch);
              }}
            >
              Deny {pendingCount}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {showHelp && (
        <Dialog title="Keyboard shortcuts" onClose={() => setShowHelp(false)} width={380}>
          <table className="pz-diff" aria-label="Keyboard shortcuts">
            <tbody>
              {SHORTCUTS.map(([key, what]) => (
                <tr key={key}>
                  <th scope="row">
                    <kbd className="pz-kbd">{key}</kbd>
                  </th>
                  <td style={{ fontFamily: 'var(--font-sans)' }}>{what}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pz-hint">Shortcuts pause while you are typing in a field.</p>
          <DialogActionsBar>
            <Button type="button" onClick={() => setShowHelp(false)}>
              Close
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
}

export default ProposalReview;
